"use client";

import * as X from "@/ui";

import { useParams } from "next/navigation";
import { localeNames } from "@/config";
import { useLocale } from "next-intl";


/**
 * Provides the current route in a logical way that allows knowing which menu
 * item to highlight.
 */
export function useRoute() {
  const path = X.usePathname();

  return X.useMemo(() => {
    return (
      path === "/" ? ["travelers", "home"] as const :
      path === "/faq" ? ["travelers", "faq"] as const :
      path === "/customer-service" ? ["travelers", "customerService"] as const :

      path === "/retail" ? ["retail", "home"] as const :
      path === "/retail/faq" ? ["retail", "faq"] as const :
      path === "/retail/customer-service" ? ["retail", "customerService"] as const :
      path === "/retail/become-partner" ? ["retail", "becomePartner"] as const :

      path === "/blog" ? ["blog", "home"] as const :
      path === "/blog/[articleSlug]" ? ["blog", "article"] as const :
      path === "/blog/tag/[categorySlug]" ? ["blog", "category"] as const :
      path === "/blog/tag" ? ["blog", "all"] as const :

      path === "/about-us" ? ["about-us"] as const :
      path === "/shipping" ? ["shipping"] as const :
      path === "/download" ? ["download"] as const :

      [] as const
    );
  }, [path]);
}


type Route = ReturnType<typeof useRoute>;
type Label = { [k in X.Locale]: React.ReactNode };
type RouteMatcher = (route: Route) => boolean;

export type Menu = typeof menu;
export type MenuSection = keyof Menu;

type Section = {
  label: Label;
  href: X.PageHref;
  match?: RouteMatcher;
  showMobile?: boolean;
  showFooter?: boolean;
  onlyFooter?: boolean;
  sub: {
    [k in string]: SectionSub;
  };
};

type SectionSub = {
  label: Label;
  href: X.PageHref;
  exitHref?: string;
  match?: (route: Route) => boolean;
  type?: "large" | "button";
  buttonPrimary?: boolean;
  buttonSecondary?: boolean;
  buttonTertiary?: boolean;
  showMobile?: boolean;
  showDesktop?: boolean;
};


export const menu = {
 "travelers": {
    href: { pathname: "/" },
    label: { en: "Travelers", fr: "Voyageurs" },
    match: route => route[0] === "travelers" && route[1] !== "faq",
    showMobile: true,
    showFooter: true,
    sub: {
      "home": {
        type: "large",
        href: { pathname: "/" },
        label: {
          en: <><strong>Don't leave your VAT</strong> behind</>,
          fr: <>La détaxe, <strong>aussi simple que votre shopping</strong></>,
        },
      },
      "home#calculator": {
        href: { pathname: "/", hash: "calculator" },
        label: {
          //en: "VAT refund calculator",
          //fr: "Notre simulateur de détaxe",
          en: "VAT Refund Simulator",
          fr: "Simulateur de Détaxe",
        },
      },
      "home#why": {
        href: { pathname: "/", hash: "why" },
        label: {
          //en: "Why choose Zapptax",
          //fr: "Pourquoi Zapptax",
          en: "Why Zapptax",
          fr: "Pourquoi Zapptax",
        },
      },
      "home#testimonials": {
        href: { pathname: "/", hash: "testimonials" },
        label: {
          //en: "What our users say",
          //fr: "Nos utilisateurs parlent de nous",
          en: "Customer Reviews",
          fr: "Avis Clients",
        },
      },
      "home#faq": {
        href: { pathname: "/", hash: "faq" },
        label: {
          //en: "Top 5 most frequently asked questions",
          //fr: "Nos top 5 des questions les plus fréquentes",
          en: "FAQs",
          fr: "FAQs",
        },
      },
      //"faq": {
        //href: { pathname: "/faq" },
        //label: {
          //en: "Full FAQ",
          //fr: "Notre FAQ complète",
        //},
      //},
      //"blog": {
        //href: { pathname: "/blog" },
        //label: {
          //en: "Our blog",
          //fr: "Notre blog",
        //},
      //},
      "customer-service": {
        href: { pathname: "/customer-service" },
        label: {
          //en: "Customer support",
          //fr: "Notre service client",
          en: "Customer Support",
          fr: "Service Clients",
        },
      },
    },
  } as Section,

 "retail": {
    href: { pathname: "/retail" },
    label: { en: "Retailers", fr: "Commerçants" },
    match: route => route[0] === "retail" && route[1] !== "faq",
    showMobile: true,
    showFooter: true,
    sub: {
      "home": {
        type: "large",
        href: { pathname: "/retail" },
        label: {
          en: <>Tax-free shopping made easy. <strong>For you</strong> and <strong>your international customers</strong></>,
          fr: <>La détaxe simplifiée, pour <strong>vous</strong> et <strong>vos clients internationaux</strong></>,
        },
      },
      "demo": {
        href: { pathname: "/retail/become-partner" },
        label: {
          //en: "See our demo",
          //fr: "Notre démo parle d'elle-même",
          en: "Become a Partner",
          fr: "Devenir Partenaire",
        },
      },
      "#why": {
        href: { pathname: "/retail", hash: "why" },
        label: {
          //en: "Why Zapptax for your business?",
          //fr: "Pourquoi Zapptax pour votre commerce ?",
          en: "Why Zapptax",
          fr: "Pourquoi Zapptax",
        },
      },
      "#testimonials": {
        href: { pathname: "/retail", hash: "testimonials" },
        label: {
          //en: "Trusted by many",
          //fr: "Ils nous font déjà confiance",
          en: "Partners Testimonials",
          fr: "Avis Partenaires",
        },
      },
      "#faq": {
        href: { pathname: "/retail", hash: "faq" },
        label: {
          //en: "Top 5 most frequently asked questions",
          //fr: "Nos top 5 des questions les plus fréquentes",
          en: "FAQs",
          fr: "FAQs",
        },
      },
      //"faq": {
        //href: { pathname: "/retail/faq" },
        //label: {
          //en: "Full FAQ",
          //fr: "Notre FAQ complète",
        //},
      //},
      //"blog": {
        //href: { pathname: "/blog" },
        //label: {
          //en: "Our blog",
          //fr: "Notre blog",
        //},
      //},
      "customer-service": {
        href: { pathname: "/retail/customer-service" },
        label: {
          //en: "Customer support",
          //fr: "Notre service client",
          en: "Customer Support",
          fr: "Service Clients",
        },
      },
    },
  } as Section,

 "shipping": {
    label: { en: "Shipping", fr: "Shipping" },
    href: { pathname: "/shipping" },
    showFooter: true,
    onlyFooter: true,
    sub: {
      "home": {
        type: "large",
        href: { pathname: "/shipping" },
        label: {
          en: <strong>Shop in Europe, ship worldwide - without VAT!</strong>,
          fr: <strong>Vos achats en Europe, livrés partout dans le monde… sans la TVA !</strong>,
        },
      },
      "homeTry": {
        href: { pathname: "/shipping" },
        label: {
          en: "Try Zapptax Shipping",
          fr: "Essayez Zapptax Shipping",
        },
      },
      "#who": {
        href: { pathname: "/shipping", hash: "who" },
        label: {
          en: "Who can use Zapptax Shipping?",
          fr: "À qui s’adresse Zapptax Shipping ?",
        },
      },
      "#pricing": {
        href: { pathname: "/shipping", hash: "pricing"  },
        label: {
          en: "What are the fees?",
          fr: "Quels sont les frais ?",
        },
      },
      "#contact": {
        href: { pathname: "/shipping", hash: "contact" },
        label: {
          en: "A customer service that’s human, helpful, and responsive",
          fr: "Un service client à votre écoute, humain et réactif",
        },
      },
    },
  } as Section,

 "about-us": {
    label: { en: "About Zapptax", fr: "Qui est Zapptax" },
    href: { pathname: "/about-us" },
    showMobile: true,
    showFooter: true,
    sub: {
      "story": {
        href: { pathname: "/about-us" },
        label: {
          en: "Our Story",
          fr: "Notre Histoire",
        },
      },
      "#mission": {
        href: { pathname: "/about-us", hash: "mission" },
        label: {
          en: "Our Mission",
          fr: "Notre Mission",
        },
      },
      "#values": {
        href: { pathname: "/about-us", hash: "values" },
        label: {
          en: "Our Values",
          fr: "Nos Valeurs",
        },
      },
      "#join": {
        href: { pathname: "/about-us", hash: "join" },
        label: {
          //en: "Join the team",
          //fr: "Rejoignez l'équipe",
          en: "Jobs",
          fr: "Jobs",
        },
      },
      "#commitments": {
        href: { pathname: "/about-us", hash: "commitments" },
        label: {
          en: "Our Commitments",
          fr: "Nos Engagements",
        },
      },
      //"#resources": {
        //href: { pathname: "/about-us", hash: "resources" },
        //label: {
          //en: "Our resources",
          //fr: "Nos ressources",
        //},
      //},
    },
  } as Section,

 "blog": {
    label: { en: "Blog", fr: "Blog" },
    href: { pathname: "/blog" },
    sub: {
    },
  } as Section,

 "faq": {
    href: { pathname: "/faq" },
    label: { en: "FAQs", fr: "FAQs" },
    match: route => (route[0] === "travelers" || route[0] === "retail") && route[1] === "faq",
    sub: {
      "travelers": {
        href: { pathname: "/faq" },
        label: {
          //en: "Travelers FAQ - all your questions answered",
          //fr: "Notre FAQ complète pour les voyageurs",
          en: "FAQs Travelers",
          fr: "FAQs Voyageurs",
        },
      },
      "retailers": {
        href: { pathname: "/retail/faq" },
        label: {
          //en: "Retailers FAQ - all your questions answered",
          //fr: "Notre FAQ complète pour les commerçants",
          en: "FAQs Retailers",
          fr: "FAQs Commerçants",
        },
      },
    },
  } as Section,

} satisfies {
  [k in string]: Section;
};


export function LanguageSelectorDesktop(props: X.PageProps<{
  hoverColor: "black" | "white";
  className?: string;
  altHrefs?: { [k in X.Locale]: X.PageHref };
}>) {
  const currentLocale = useLocale();
  const pathname = X.usePathname();
  const params = useParams();

  return (
    <div className={X.clsx("relative group/language text-px13 pl-4", props.className)}>
      <button className="flex items-center space-x-1 py-2">
        <X.Icon.Globe className="h-5 w-5" />
        <span>{localeNames[currentLocale]}</span>
        <X.Icon.ChevronDown className="h-4 w-4" />
      </button>

      <div className={X.clsx(
        "absolute hidden group-hover/language:flex",
        "flex-col top-full left-0 rounded-md pb-2 w-32 z-10 px-2",
      )}>
        {
          Object.entries(localeNames)
            .filter(([locale]) => locale !== currentLocale)
            .map(([locale, name]) => (
              <X.Link
                key={locale}
                rel="alternate"
                locale={locale as X.Locale}
                hrefLang={locale}
                className={X.clsx(
                  "block px-8 py-2",
                  "text-gray hover:text-black text-px13",
                  props.hoverColor === "white" ? "hover:text-white" : "hover:text-black",
                )}
                href={
                  props.altHrefs
                    && props.altHrefs[locale as X.Locale]
                    || { pathname, params } as any
                }
              >
                {name}
              </X.Link>
            ))
        }
      </div>
    </div>
  );
}


export function LanguageSelectorMobile(props: X.PageProps<{
  backgroundColor: "black" | "white";
  className?: string;
  altHrefs?: { [k in X.Locale]: X.PageHref };
}>) {
  const currentLocale = useLocale();
  const pathname = X.usePathname();
  const params = useParams();

  const [isOpen, setIsOpen] = X.useState(false);

  return (
    <div className={X.clsx("relative text-[11px]", props.className)}>
      <button className="flex items-center space-x-1 py-2" onClick={() => setIsOpen(wasOpen => !wasOpen)}>
        <X.Icon.Globe className="h-5 w-5" />
        <span>{localeNames[currentLocale]}</span>
        <X.Icon.ChevronDown className="h-4 w-4" />
      </button>

      <div className={X.clsx(
        "absolute flex-col top-full left-0 rounded-md pb-2 w-32 z-10 px-2",
        isOpen ? "flex" : "hidden",
        props.backgroundColor === "white" ? "bg-white" : "bg-black",
      )}>
        {
          Object.entries(localeNames)
            .filter(([locale]) => locale !== currentLocale)
            .map(([locale, name]) => (
              <X.Link
                key={locale}
                rel="alternate"
                locale={locale as X.Locale}
                hrefLang={locale}
                className={X.clsx(
                  "block px-4 py-2",
                  "text-gray text-[11px]"
                )}
                href={
                  props.altHrefs
                    ? props.altHrefs[locale as X.Locale]
                    : { pathname, params } as any
                }
              >
                {name}
              </X.Link>
            ))
        }
      </div>
    </div>
  );
}
